import {Alert, Autocomplete, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, FormControlLabel, FormLabel, Radio, RadioGroup, TextField} from '@mui/material';
import React, {useEffect, useState} from 'react';
import {OrganizationController} from '../../../api/organization/organization.controller';
import {StatusCallController} from '../../../api/status-calls/status-call.controller';
import {OrganizationData} from '../../../model/organization/organization-data';
import {StatusCallsPageHooks} from '../state/status-calls-page-hooks';
import {hoursForDuration} from '../utils/status-call.utils';

type Props = {
  open: boolean
  prefilledScheduleAt: string | null
  onClose: () => void
  onSuccess: () => Promise<void>
  translateLocal: (key: string, params?: Record<string, unknown>) => string
};

const splitIso = (iso: string | null): { date: string; time: string } => {
  if (!iso) {
    const now = new Date();
    now.setMinutes(now.getMinutes() < 30 ? 30 : 0, 0, 0);
    if (now.getMinutes() === 0) now.setHours(now.getHours() + 1);
    const pad = (n: number) => String(n).padStart(2, '0');
    return {
      date: `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`,
      time: `${pad(now.getHours())}:${pad(now.getMinutes())}`,
    };
  }
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return {
    date: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`,
    time: `${pad(d.getHours())}:${pad(d.getMinutes())}`,
  };
};

export const ScheduleCallModal = ({open, prefilledScheduleAt, onClose, onSuccess, translateLocal}: Props) => {
  const [organizations, setOrganizations] = useState<OrganizationData[]>([]);
  const [organization, setOrganization] = useState<OrganizationData | null>(null);
  const initial = splitIso(prefilledScheduleAt);
  const [date, setDate] = useState(initial.date);
  const [time, setTime] = useState(initial.time);
  const [durationMinutes, setDurationMinutes] = useState<30 | 60>(30);
  const [title, setTitle] = useState('');
  const [notes, setNotes] = useState('');
  const [meetingLink, setMeetingLink] = useState('');
  const [participants, setParticipants] = useState<string[]>([]);
  const [participantInput, setParticipantInput] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const [account, , refreshAccount] = StatusCallsPageHooks.useStatusCallAccount(organization?.id ?? null);

  const pad = (n: number) => String(n).padStart(2, '0');
  const now = new Date();
  const todayStr = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
  const minTimeForToday = `${pad(now.getHours())}:${pad(now.getMinutes())}`;
  const minTime = date === todayStr ? minTimeForToday : undefined;
  const isPast = new Date(`${date}T${time}:00`).getTime() < now.getTime();

  useEffect(() => {
    if (open) {
      OrganizationController.fetchOrganizations().then(setOrganizations);
      const reset = splitIso(prefilledScheduleAt);
      setDate(reset.date);
      setTime(reset.time);
      setDurationMinutes(30);
      setTitle('');
      setNotes('');
      setMeetingLink('');
      setParticipants([]);
      setParticipantInput('');
      setError(null);
    }
  }, [open, prefilledScheduleAt]);

  const required = hoursForDuration(durationMinutes);
  const available = account?.hoursAvailable ?? 0;
  const remaining = available - required;
  const insufficient = !!organization && remaining < 0;

  const addParticipant = () => {
    const value = participantInput.trim();
    if (value && /\S+@\S+\.\S+/.test(value) && !participants.includes(value)) {
      setParticipants([...participants, value]);
      setParticipantInput('');
    }
  };

  const handleSubmit = async () => {
    if (!organization) return;
    if (insufficient) {
      setError(translateLocal('error.insufficient-pool', {available, required}));
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await StatusCallController.createStatusCall({
        organizationId: organization.id,
        scheduledAt: `${date}T${time}:00`,
        durationMinutes,
        title: title || null,
        notes: notes || null,
        meetingLink: meetingLink || null,
        participantEmails: participants,
      });
      await onSuccess();
      onClose();
    } catch (e: any) {
      if (e?.response?.status === 409) {
        setError(translateLocal('error.slot-conflict'));
      } else {
        setError(e?.message ?? 'Error');
      }
    } finally {
      setSubmitting(false);
      await refreshAccount();
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{translateLocal('modal.schedule.title')}</DialogTitle>
      <DialogContent>
        <Box display="flex" flexDirection="column" gap={2} pt={1}>
          <Autocomplete
            options={organizations}
            value={organization}
            onChange={(_, v) => setOrganization(v)}
            getOptionLabel={(o) => o.name}
            renderInput={(params) => <TextField {...params} label={translateLocal('modal.schedule.organization')}/>}
          />
          {organization && account && (
            <Alert severity={insufficient ? 'error' : 'info'}>
              {translateLocal('modal.schedule.pool-info', {available, remaining: Math.max(0, remaining)})}
            </Alert>
          )}
          <Box display="flex" gap={2}>
            <TextField
              label={translateLocal('modal.schedule.date')}
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              InputLabelProps={{shrink: true}}
              inputProps={{min: todayStr}}
              fullWidth
            />
            <TextField
              label={translateLocal('modal.schedule.time')}
              type="time"
              value={time}
              onChange={(e) => setTime(e.target.value)}
              InputLabelProps={{shrink: true}}
              inputProps={{step: 1800, min: minTime}}
              fullWidth
            />
          </Box>
          <FormControl>
            <FormLabel>{translateLocal('modal.schedule.duration')}</FormLabel>
            <RadioGroup
              row
              value={durationMinutes}
              onChange={(e) => setDurationMinutes(Number(e.target.value) as 30 | 60)}
            >
              <FormControlLabel value={30} control={<Radio/>} label={translateLocal('modal.schedule.duration30')}/>
              <FormControlLabel value={60} control={<Radio/>} label={translateLocal('modal.schedule.duration60')}/>
            </RadioGroup>
          </FormControl>
          <TextField label={translateLocal('modal.schedule.title-field')} value={title} onChange={(e) => setTitle(e.target.value)}/>
          <TextField label={translateLocal('modal.schedule.notes')} value={notes} onChange={(e) => setNotes(e.target.value)} multiline rows={3}/>
          <TextField label={translateLocal('modal.schedule.meeting-link')} value={meetingLink} onChange={(e) => setMeetingLink(e.target.value)}/>
          <Box>
            <Box display="flex" gap={1} mb={1} flexWrap="wrap">
              {participants.map(p => (
                <Chip key={p} label={p} onDelete={() => setParticipants(participants.filter(x => x !== p))}/>
              ))}
            </Box>
            <TextField
              label={translateLocal('modal.schedule.participants')}
              value={participantInput}
              onChange={(e) => setParticipantInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ',') {
                  e.preventDefault();
                  addParticipant();
                }
              }}
              onBlur={addParticipant}
              fullWidth
            />
          </Box>
          {error && <Alert severity="error">{error}</Alert>}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{translateLocal('modal.schedule.cancel')}</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!organization || submitting || insufficient || isPast}
        >
          {translateLocal('modal.schedule.submit')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
